import { createFileRoute, Link } from "@tanstack/react-router";
import { Coffee, Home, Car, Phone } from "lucide-react";
import { breadcrumbSchema, canonical, faqPageSchema } from "@/lib/seo";

export const Route = createFileRoute("/blog/off-season-stays-lake-erie")({
  head: () => ({
    meta: [
      { title: "Quiet Off-Season Stays Near Lake Erie - Malak BnB Resort" },
      { name: "description", content: "A quiet off-season getaway near Lake Erie at Malak BnB Resort in Crystal Beach, Ontario. Fewer crowds, cozy rooms, and breakfast every morning." },
      { property: "og:title", content: "Quiet Off-Season Stays Near Lake Erie - Malak BnB Resort" },
      { property: "og:description", content: "Crystal Beach slows down in the off season. Here is what a quiet stay at Malak BnB Resort looks like." },
      { property: "og:url", content: "https://malakbnbresort.ca/blog/off-season-stays-lake-erie" },
      { property: "og:image", content: "/images/cozy-lake-winter.webp" },
      { "script:ld+json": faqPageSchema(FAQ) },
      {
        "script:ld+json": breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Blog", path: "/blog" },
          { name: "Quiet Off-Season Stays Near Lake Erie", path: "/blog/off-season-stays-lake-erie" },
        ]),
      },
    ],
    links: [canonical("/blog/off-season-stays-lake-erie")],
  }),
  component: OffSeasonPage,
});

const FAQ = [
  {
    q: "Is Malak BnB Resort open in the off season?",
    a: "Yes. Malak BnB Resort is open year round, with the same continental breakfast, comfortable rooms, and personal booking process available in the off season as during summer.",
  },
  {
    q: "What is there to do near Crystal Beach in the off season?",
    a: "Quiet walks along Bay Beach and Ridge Beach, a slower pace around town, and day trips to Niagara Falls, about 30 minutes away, are all worth doing when the summer crowds are gone.",
  },
  {
    q: "Do room rates change in the off season at Malak BnB Resort?",
    a: "Rates at Malak BnB Resort vary by season. Current pricing is always shown on the rooms and rates page before you submit a reservation request.",
  },
  {
    q: "Is breakfast still included during an off-season stay?",
    a: "Yes. A continental breakfast buffet is included with every stay at Malak BnB Resort, year round.",
  },
];

function OffSeasonPage() {
  return (
    <div>
      {/* Hero */}
      <section className="relative h-[55vh] min-h-[380px] overflow-hidden">
        <img
          src="/images/cozy-lake-winter.webp"
          alt="Cozy living room with fireplace and a lake view"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/65 via-foreground/20 to-transparent" />
        <div className="relative z-10 flex h-full items-end pb-16 px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl w-full">
            <div className="flex items-center gap-2 text-white/70 text-sm mb-3">
              <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
              <span>/</span>
              <span>Off-Season Stays</span>
            </div>
            <p className="text-white/70 font-semibold text-sm uppercase tracking-widest mb-3">Lake Erie, Ontario</p>
            <h1 className="text-4xl sm:text-5xl font-heading text-white leading-tight max-w-2xl">
              Quiet Off-Season Stays Near Lake Erie
            </h1>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg text-muted-foreground space-y-5 leading-relaxed">
            <p>
              There is a version of Crystal Beach that most summer visitors never see. Once the season turns, the
              town empties out, the lake gets moody and grey in a way that is its own kind of beautiful, and the
              whole place slows down to a pace that summer never allows.
            </p>
            <p>
              Malak BnB Resort stays open through it, with the same breakfast and the same warm rooms, minus the
              crowds. It is a different trip than a July weekend, and for some travellers, a better one.
            </p>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Photo by Matt Barnard via{" "}
            <a href="https://www.pexels.com/photo/warm-living-room-with-christmas-decorations-fireplace-and-lake-view-perfect-for-holiday-themes-36383159/" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">
              Pexels
            </a>
          </p>
        </div>
      </section>

      {/* Why it works */}
      <section className="pb-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Home className="h-5 w-5 text-primary" />
                <p className="text-primary font-semibold text-sm uppercase tracking-wider">Why Off Season Works</p>
              </div>
              <h2 className="text-2xl sm:text-3xl font-heading text-foreground leading-snug mb-4">
                Fewer People, More Room to Breathe
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Bay Beach becomes a place for a walk rather than a crowd. Restaurants that need reservations in
                summer will happily seat you without one. And the indoor and outdoor lounge areas at Malak BnB
                Resort become the kind of quiet spot where a book and a coffee are enough for an afternoon.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img src="/images/gallery-lounge.webp" alt="Indoor lounge area at Malak BnB Resort" className="w-full object-cover aspect-[4/3]" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* What to do */}
      <section className="py-20 bg-card">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Car className="h-5 w-5 text-primary" />
            <p className="text-primary font-semibold text-sm uppercase tracking-wider">While You're Here</p>
          </div>
          <h2 className="text-3xl font-heading text-foreground mb-6">What to Do When It's Quiet</h2>
          <p className="text-muted-foreground leading-relaxed">
            A day trip to Niagara Falls is even better in the off season, with shorter lines and easier parking
            along the Niagara Parkway. Closer to home, a walk along Bay Beach or Ridge Beach in cooler weather has
            its own appeal, and the town's restaurants are easier to get into without planning ahead.
          </p>
        </div>
      </section>

      {/* Stay */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img src="/images/gallery-bedroom-warm.webp" alt="Warmly lit bedroom at Malak BnB Resort" className="w-full object-cover aspect-[4/3]" loading="lazy" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Coffee className="h-5 w-5 text-primary" />
                <p className="text-primary font-semibold text-sm uppercase tracking-wider">Where to Stay</p>
              </div>
              <h2 className="text-3xl font-heading text-foreground leading-snug mb-6">
                A Warm Room at Malak BnB Resort
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-8">
                Every room keeps its comfort year round: continental breakfast each morning, air conditioning and
                heating as the season needs, and a family-owned team that reviews and confirms every reservation
                request personally.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/book" className="inline-flex items-center justify-center rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors">
                  Request to Book
                </Link>
                <Link to="/rooms" className="inline-flex items-center justify-center rounded-full border border-border px-8 py-3 text-sm font-semibold text-foreground hover:bg-muted transition-colors">
                  View Rooms and Rates
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-card">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">Common Questions</p>
            <h2 className="text-3xl font-heading text-foreground">Planning an Off-Season Trip</h2>
          </div>
          <div className="space-y-8">
            {FAQ.map(({ q, a }) => (
              <div key={q} className="border-b border-border pb-8 last:border-0 last:pb-0">
                <h3 className="font-heading text-lg text-foreground mb-3">{q}</h3>
                <p className="text-muted-foreground leading-relaxed text-sm">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-foreground">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <p className="text-primary-foreground/50 font-semibold text-sm uppercase tracking-widest mb-4">Crystal Beach, Ontario</p>
          <h2 className="text-3xl sm:text-4xl font-heading text-primary-foreground leading-tight">
            Request a quiet stay at Malak BnB Resort
          </h2>
          <p className="mt-5 text-primary-foreground/70 max-w-lg mx-auto leading-relaxed">
            Submit a reservation request and the team will personally confirm availability and send payment details directly.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/book" className="inline-flex items-center justify-center rounded-full bg-primary px-10 py-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors shadow-lg">
              Request Your Stay
            </Link>
            <a href="tel:+12899298055" className="inline-flex items-center gap-2 text-primary-foreground/70 hover:text-primary-foreground text-sm font-medium transition-colors">
              <Phone className="h-4 w-4" />
              1-289-929-8055
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
